import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useRelationOperations from "../../features/operations/useRelationOperations";
import { itemRoutingApi } from "../../features/itemRoutings/itemRoutingApi";
import { machineCapabilityApi } from "../../features/machineCapabilities/machineCapabilityApi";
import { assertSuccessfulResponse, getRequestErrorMessage } from "../../services/apiMessages";

// Read all pages so editing never silently drops mappings beyond the first page.
export const loadAllMappingRows = async (dispatch, endpoint, filters) => {
  const rows = [];
  for (let page = 1; ; page += 1) {
    const response = await dispatch(endpoint.initiate({ ...filters, page, limit: 1000 }, {
      subscribe: false, forceRefetch: true,
    })).unwrap();
    if (response?.status === "failed" && response?.message !== "No Record Found") {
      assertSuccessfulResponse(response);
    }
    const batch = response?.data || [];
    if (!Array.isArray(batch)) throw new Error("Unable to load operation mappings.");
    rows.push(...batch);
    if (!batch.length || rows.length >= (response?.meta_data?.total_records ?? rows.length)) return rows;
  }
};

const createRow = (operationId = "") => ({ key: `${Date.now()}_${Math.random().toString(36).slice(2)}`, operation_id: operationId, relation_type: "" });

const uid = (row) => row?.uid || row?._id || row?.id;

const useOperationMapping = (kind, plantId) => {
  const dispatch = useDispatch();
  const [relationType, setRelationType] = useState("");
  const operationQuery = useRelationOperations(plantId, relationType);
  const [rows, setRows] = useState([]);
  const value = rows.map((row) => row.operation_id);
  const [parameters, setParameters] = useState([]);
  const [itemId, setItemId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const source = useRef(null);
  const isItem = kind === "item";
  const idField = isItem ? "item_id" : "machine_id";
  const listEndpoint = isItem ? itemRoutingApi.endpoints.getItemRoutings : machineCapabilityApi.endpoints.getMachineCapabilities;

  useEffect(() => {
    generation.current += 1;
    return () => { generation.current += 1; };
  }, [plantId]);

  const load = useCallback(async (row = null, preserveRelation = false) => {
    source.current = row;
    setItemId(row?.item_id || "");
    const version = ++generation.current;
    const selectedPlant = row?.plant_id || plantId;
    setRows([]);
    setParameters([]);
    if (!preserveRelation) setRelationType(row?.relation_type || "");
    setError("");
    setIsLoading(true);
    try {
      const mappings = row?.[idField]
        ? await loadAllMappingRows(dispatch, listEndpoint, { plant_id: selectedPlant, [idField]: row[idField] })
        : [];
      if (version !== generation.current) return;
      setRows([...mappings].sort((a, b) => (a.position || 0) - (b.position || 0)).map((mapping) => createRow(mapping.operation_id)));
    } catch (failure) {
      if (version === generation.current) setError(getRequestErrorMessage(failure, "Unable to load operation mappings."));
    } finally {
      if (version === generation.current) setIsLoading(false);
    }
  }, [dispatch, idField, listEndpoint, plantId]);

  const save = useCallback(async (record) => {
    if (!record?.[idField]) throw new Error("The saved record did not include its ID. Reopen it from the list to map operations.");
    if (isItem) setItemId(record.item_id);
    const filters = { plant_id: record.plant_id || plantId, [idField]: record[idField] };
    const current = await loadAllMappingRows(dispatch, listEndpoint, filters);
    const mutate = async (endpoint, args) => {
      const request = dispatch(endpoint.initiate(args));
      try { return assertSuccessfulResponse(await request.unwrap()); }
      finally { request.reset(); }
    };
    if (isItem && value.length) {
      await mutate(itemRoutingApi.endpoints.updateItemRoutingSeries, {
        ...filters,
        operations: value.map((operation_id, index) => ({ operation_id, position: index + 1 })),
      });
      for (const { key, saved, ...parameter } of parameters) {
        if (saved || !value.includes(parameter.operation_id)) continue;
        try {
          await mutate(itemRoutingApi.endpoints.addRoutingParameter, { ...parameter, ...filters });
        } catch (failure) {
          throw new Error(`Item routing saved, but parameter ${parameter.parameter_name} could not be saved. Retry to save the remaining parameters. ${getRequestErrorMessage(failure)}`);
        }
        setParameters((current) => current.map((entry) => entry.key === key ? { ...entry, saved: true } : entry));
      }
      return;
    }
    if (!isItem) {
      for (const operation_id of value) {
        if (!current.some((row) => row.operation_id === operation_id)) {
          await mutate(machineCapabilityApi.endpoints.addMachineCapability, { ...filters, operation_id });
        }
      }
    }
    for (const row of current) {
      if (value.includes(row.operation_id)) continue;
      if (!uid(row)) throw new Error("A mapping is missing its record ID. Reload the form and try again.");
      await mutate(isItem ? itemRoutingApi.endpoints.deleteItemRouting : machineCapabilityApi.endpoints.deleteMachineCapability, uid(row));
    }
  }, [dispatch, idField, isItem, listEndpoint, parameters, plantId, value]);

  const onChange = (nextValue) => {
    setRows((current) => {
      const remaining = [...current];
      return nextValue.map((id) => {
        const index = remaining.findIndex((row) => row.operation_id === id);
        return index < 0 ? createRow(id) : remaining.splice(index, 1)[0];
      });
    });
    setParameters((current) => current.filter((parameter) => nextValue.includes(parameter.operation_id)));
  };

  const addParameter = (operationId, parameter) => {
    setParameters((current) => [...current, {
      ...parameter,
      operation_id: operationId,
      key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    }]);
  };

  const removeParameter = (key) => {
    setParameters((current) => current.filter((parameter) => parameter.key !== key || parameter.saved));
  };

  return {
    options: operationQuery.options, value, onChange, rows,
    moveRow: (index, offset) => setRows((current) => {
      const next = [...current];
      [next[index], next[index + offset]] = [next[index + offset], next[index]];
      return next;
    }),
    removeRow: (key) => {
      const previous = rows.find((row) => row.key === key);
      setRows((current) => current.filter((row) => row.key !== key));
      setParameters((current) => current.filter((parameter) => parameter.operation_id !== previous?.operation_id));
    },
    changeRow: (key, updates) => {
      const previous = rows.find((row) => row.key === key);
      setRows((current) => current.map((row) => row.key === key ? { ...row, ...updates } : row));
      if (previous && updates.operation_id !== previous.operation_id) {
        setParameters((current) => current.filter((parameter) => parameter.operation_id !== previous.operation_id));
      }
    },
    isLoading: isLoading || operationQuery.isLoading,
    error: error || operationQuery.error, load, save,
    retry: () => error ? load(source.current, true) : operationQuery.retry(),
    plantId, itemId, relationType,
    hasInvalidOperations: !isItem && value.some((id) => id && !operationQuery.options.some((option) => option.value === id)),
    changeRelationType: (nextType) => {
      if (nextType === relationType) return;
      setRelationType(nextType);
      if (!isItem) {
        setRows([]);
        setParameters([]);
      }
    },
    supportsParameters: isItem, parameters, addParameter, removeParameter,
  };
};

export default useOperationMapping;
